import './FinanceCalculatorResultsTable.scss';
import { Col, Row } from "reactstrap";
import { useState } from "react";
import { IFinanceProductPublicNotification, IFinanceProductResults, IFinanceProductResultsQuote, IFinanceProductResultsQuoteFees } from '../../../../models/codeweavers.model';
import { Alert, Box, Button, Card, CardContent, Typography } from '@mui/material';
import { Theme, createTheme } from '@mui/material/styles'
import { formatCurrency } from '../../../shared/utils';
import { getDesignTokens } from '../../../shared/palette';
import { IFinanceCalculatorState, IProductMinValueSet } from '../../../../models/finance-calculator.model';
import config from '../../../../config';
import React from 'react';

export const getSplit = (key: string, first: boolean) => {
    const delim = " - ";
    if (key.indexOf(delim) !== -1) {
        const split = key.split(delim, 2);
        return first ? split[0] : split[1];
    } else {
        return key;
    }
};

export const getLenderFromKey = (key: string) => {
    return getSplit(key, false);
};

export const getProductFromKey = (key: string) => {
    return getSplit(key, true);
};

const getShadowSelected = (product: string, theme: Theme) => {
    return "3px 7px 15px -2px " + (product === "PCP" ? theme.palette.primary.main : theme.palette.secondary.main);
};

const getCompletionFee = (quote: IFinanceProductResultsQuote | undefined) => {
    let res: number = 0;
    quote?.Fees?.map((fee: IFinanceProductResultsQuoteFees) => {
        if ((fee?.Profile === "LAST") && (fee?.FeeType === "Option") && (fee?.Amount !== undefined)) {
            res = fee?.Amount;
        }
        return undefined;
    });
    return res;
};

const getArrangementFee = (quote: IFinanceProductResultsQuote | undefined) => {
    let res: number = 0;
    quote?.Fees?.map((fee: IFinanceProductResultsQuoteFees) => {
        if ((fee?.Profile === "FIRST") /*&& (fee?.FeeType === "Option")*/ && (fee?.Amount !== undefined)) {
            res = fee?.Amount;
        }
        return undefined;
    });
    return res;
};

const getLowestValues = (results: IFinanceProductResults[] | undefined) => {
    let r: IProductMinValueSet = { hp: { minValue: 0, minIndex: -1 }, pcp: { minValue: 0, minIndex: -1 } };
    if (results) {
        results.map((res: IFinanceProductResults, i: number) => {
            const prod = getProductFromKey(res.Key);
            if (res.Quote.RegularPayment) {
                if (prod === "PCP") {
                    if ((r.pcp.minIndex === -1) || (res.Quote.RegularPayment < r.pcp.minValue)) {
                        r.pcp.minValue = res.Quote.RegularPayment;
                        r.pcp.minIndex = i;
                    }
                } else {
                    if ((r.hp.minIndex === -1) || (res.Quote.RegularPayment < r.hp.minValue)) {
                        r.hp.minValue = res.Quote.RegularPayment;
                        r.hp.minIndex = i;
                    }
                }

            }
            return undefined;
        });
    }
    // console.log("getLowestValues: " + JSON.stringify(r));
    return r;
};

const lgMinHeight = 80;
const mdMinHeight = 85;
const smMinHeight = 90;
const padBottom = 5;

const FinanceCalculatorResultsTableDetail = ({ detail, selectedIndex, theme, onApply }: { detail: IFinanceProductResults, selectedIndex: number, theme: Theme, onApply?: React.Dispatch<any> }) => {
    const product = getProductFromKey(detail.Key);
    const lender = getLenderFromKey(detail.Key);
    const shadowSelected = getShadowSelected(product, theme);

    return (
        <>
            <Box sx={{ minHeight: { xs: 0, sm: (smMinHeight + padBottom) * selectedIndex, md: (mdMinHeight + padBottom) * selectedIndex, lg: (lgMinHeight + padBottom) * selectedIndex } }}>
            </Box>
            <div className="table-detail">
                <Card sx={{ boxShadow: shadowSelected }}>
                    <CardContent>
                        <Typography sx={{ fontSize: "80%" }} color="text.secondary" gutterBottom>
                            <Row>
                                <Col className={"key-detail " + product + "-" + theme.palette.mode}>
                                    <Typography>
                                        <span>{product}&nbsp;{lender}</span>
                                    </Typography>
                                </Col>
                            </Row>
                            <Row>
                                <Col className="label-detail">Cash price</Col>
                                <Col className="value-detail">{formatCurrency(detail.Quote.CashPrice)}</Col>
                            </Row>
                            <Row>
                                <Col className="label-detail">Deposit</Col>
                                <Col className="value-detail">{formatCurrency(detail.Quote.Deposit)}</Col>
                            </Row>
                            <Row>
                                <Col className="label-detail">Amount of credit</Col>
                                <Col className="value-detail">{formatCurrency(detail.Quote.AmountOfCredit)}</Col>
                            </Row>
                            <Row>
                                <Col className="label-detail">{detail.Quote.Term - (product === "PCP" ? 1 : 0)} monthly rentals</Col>
                                <Col className="value-detail">{formatCurrency(detail.Quote.RegularPayment)}</Col>
                            </Row>
                            <Row>
                                <Col className="label-detail">Arrangement Fee</Col>
                                <Col className="value-detail">{formatCurrency(getArrangementFee(detail.Quote))}</Col>
                            </Row>
                            <Row>
                                <Col className="label-detail">Completion Fee</Col>
                                <Col className="value-detail">{formatCurrency(getCompletionFee(detail.Quote))}</Col>
                            </Row>
                            {product === "PCP" && (
                                <Row>
                                    <Col className="label-detail">Final rental</Col>
                                    <Col className="value-detail">{formatCurrency(detail.Quote.FinalPayment)}</Col>
                                </Row>
                            )}
                            <Row>
                                <Col className="label-detail">Interest charges</Col>
                                <Col className="value-detail">{formatCurrency(detail.Quote.TotalChargeForCredit)}</Col>
                            </Row>
                            <Row>
                                <Col className="label-detail">Total amount payable</Col>
                                <Col className="value-detail">{formatCurrency(detail.Quote.TotalAmountPayable)}</Col>
                            </Row>
                            <Row>
                                <Col className="label-detail">APR</Col>
                                <Col className="value-detail">{detail.Quote.Apr}%</Col>
                            </Row>
                            {detail.Notifications && detail.Notifications.Public && detail.Notifications.Public.map((note: IFinanceProductPublicNotification, i: number) =>
                                <Row className="alert-message">
                                    <Alert variant="outlined" severity="warning">
                                        {note.Message}
                                    </Alert>
                                </Row>
                            )}
                            { onApply && (
                            <Row className="apply-now">
                                <Col className="right-align">
                                    <Button type="button" variant="contained" color="primary" onClick={onApply}>Apply Now</Button>
                                </Col>
                            </Row>
                            )}
                        </Typography>
                </CardContent>
                </Card>
            </div>
        </>
    );
};

const FinanceCalculatorResultsTableRow = ({ row, selected, theme, lowest }: { row: IFinanceProductResults, selected: boolean, theme: Theme, lowest: boolean }) => {
    const product = getProductFromKey(row.Key);
    const lender = getLenderFromKey(row.Key);
    const shadowSelected = getShadowSelected(product, theme);
    const shadowNormal = "1px 3px 5px -2px "; // + theme.palette.text.primary;

    return (
        <div className="table-row">
            <Card sx={{ marginBottom: "5px", minHeight: { xs: 0, sm: smMinHeight, md: mdMinHeight, lg: lgMinHeight }, boxShadow: selected ? shadowSelected : shadowNormal, color: selected ? "text.primary" : "text.secondary" }}>
                <CardContent>
                    <Row>
                        <Col xs={6} sm={5}>
                            <Row>
                                <Typography sx={{ fontSize: "65%" }} color="text.primary">
                                    <Col><span className={"product " + product + "-" + theme.palette.mode}>{product}</span></Col>
                                </Typography>
                            </Row>
                            <Row>
                                <Col>
                                   {lowest ? (
                                        <span className="lowest">{"Lowest " + product}</span>
                                   ) : (<span>&nbsp;</span>)}
                                </Col>
                            </Row>
                            <Row>
                                <Typography sx={{ fontSize: "80%", fontWeight: (selected ? "bold" : "inherit") }} color="text.secondary">
                                    <Col><span className={"lender " + lender}>{lender}</span></Col>
                                </Typography>
                            </Row>
                        </Col>
                        <Col className="class-col">
                            <Typography sx={{ fontSize: "70%", fontWeight: (selected ? "bold" : "inherit") }} color="text.secondary">
                                <Row>
                                    <Col>
                                        Monthly: {formatCurrency(row.Quote?.RegularPayment)}
                                    </Col>
                                </Row>
                                {product === "PCP" && (
                                    <Row>
                                        <Col>Final rental: {formatCurrency(row.Quote?.FinalPayment)}</Col>
                                    </Row>
                                )}
                                <Row>
                                    <Col>{row.Quote?.Term} months @ {row.Quote?.Apr}% APR</Col>
                                </Row>
                            </Typography>
                        </Col>
                    </Row>
                </CardContent>
            </Card>
        </div>
    );
};

const FinanceCalculatorResultsTable = ({ state, updateState, onApply }: { state: IFinanceCalculatorState, updateState: React.Dispatch<any>, onApply?: React.Dispatch<any> }) => {
    // const [state, setState] = useState<any>({ selectedIndex: -1 });
    const [innerstate, setInnerState] = useState<any>({ showErrors: false, errorLevel: undefined });
    // let showErrors: boolean = false;
    // let errorLevel: string | undefined = undefined;
    const baseTheme = createTheme(getDesignTokens(state.mode));

    const lowestValues = getLowestValues(state.resultsdata);

    const onRowSelect = (i: number) => {
        updateState({ ...state, selectedIndex: i });
    }

    React.useEffect(() => {

        setInnerState({ showErrors: (config.REACT_APP_CW_SHOW_ERRORS === "true"), errorLevel: config.REACT_APP_CW_ERROR_LEVEL });

    }, []);


    return (
        <>
            <Row sx={{ m: 0 }}>
                <Col sm={6} md={5} lg={4}>
                    {state.resultsdata && state.resultsdata.map((res: IFinanceProductResults, i: number) =>
                        <>
                            <div onClick={() => onRowSelect(i)} className={state.selectedIndex === i ? "row-selected" : "row-normal"}>
                                <FinanceCalculatorResultsTableRow row={res} selected={state.selectedIndex === i} theme={baseTheme} lowest={(lowestValues.hp.minIndex === i) || (lowestValues.pcp.minIndex === i)} />
                            </div>
                        </>
                    )}
                </Col>
                <Col sm={6} md={5} lg={4}>
                    {(state.selectedIndex >= 0) && state.resultsdata && (state.selectedIndex <= state.resultsdata.length) && (
                        <FinanceCalculatorResultsTableDetail detail={state.resultsdata[state.selectedIndex]} selectedIndex={state.selectedIndex} theme={baseTheme} onApply={onApply} />
                    )}
                </Col>
            </Row>
            {innerstate.showErrors && (innerstate.errorLevel !== "simple") && (
                <Row sx={{ m: 1 }}>
                    <Col sm={6} md={4} lg={3}>
                        {state.errordata && state.errordata.map((err: IFinanceProductResults, i: number) =>
                            <>
                                <Alert variant="outlined" severity="error">
                                    {err.Key}: {(innerstate.errorLevel === "technical" ? err.Error?.TechnicalMessage : (innerstate.errorLevel === "dealer" ? err.Error?.DealerMessage : err.Error?.Code?.toString()))}
                                </Alert>
                            </>
                        )}
                    </Col>
                </Row>
            )}
            {innerstate.showErrors && (innerstate.errorLevel === "simple") && (
                <Row sx={{ m: 1 }}>
                    <Col sm={6} md={4} lg={3}>
                        <Alert variant="outlined" severity="error">
                            Some products returned an error
                        </Alert>
                    </Col>
                </Row>
            )}
        </>
    );
};

export default FinanceCalculatorResultsTable;
